import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  addDoc, 
  updateDoc, 
  serverTimestamp,
  query,
  where,
  onSnapshot,
  setDoc
} from "firebase/firestore";

const firestore = getFirestore();
const db = firestore;
/**
 * ✅ FIXED: Get user-specific settings including rates, promos, referrals, and fees
 */
export const getUserSettings = async (userId) => {
  try {
    console.log("🔍 getUserSettings called for:", userId);
    
    const userSettingsDoc = await getDoc(doc(firestore, "UserSettings", userId));
    const globalSettingsDoc = await getDoc(doc(firestore, "Settings", "general"));
    
    const globalRate = globalSettingsDoc.exists() ? globalSettingsDoc.data().monthlyGrowthRate || 10 : 10;
    console.log("🌍 Global rate:", globalRate);
    
    if (userSettingsDoc.exists()) {
      const rawUserSettings = userSettingsDoc.data();
      console.log("📥 Raw user settings from UserSettings collection:", {
        monthlyRate: rawUserSettings.monthlyRate,
        adminFeePercentage: rawUserSettings.adminFeePercentage,
        adminFeeFixed: rawUserSettings.adminFeeFixed
      });
      
      const processedSettings = {
        // User-specific rate or fallback to global
        monthlyRate: rawUserSettings.monthlyRate !== undefined ? rawUserSettings.monthlyRate : globalRate,
        
        // Promo periods
        promoRates: rawUserSettings.promoRates || [],
        
        // Referral settings
        referralBonus: rawUserSettings.referralBonus || 0,
        totalReferrals: rawUserSettings.totalReferrals || 0,
        
        // Admin fees
        adminFeePercentage: rawUserSettings.adminFeePercentage || 0,
        adminFeeFixed: rawUserSettings.adminFeeFixed || 0,
        
        // Rate history
        rateHistory: rawUserSettings.rateHistory || [],
        
        // Other settings
        customSettings: rawUserSettings.customSettings || {},
        
        // Fallback indicators
        isUsingGlobalRate: rawUserSettings.monthlyRate === undefined,
        lastUpdated: rawUserSettings.lastUpdated,
        updatedBy: rawUserSettings.updatedBy,
        feeReason: rawUserSettings.feeReason || null,
        lastReferralBonus: rawUserSettings.lastReferralBonus || null
      };
      
      console.log("📤 Final processed settings:", {
        monthlyRate: processedSettings.monthlyRate,
        adminFeePercentage: processedSettings.adminFeePercentage,
        adminFeeFixed: processedSettings.adminFeeFixed,
        isUsingGlobalRate: processedSettings.isUsingGlobalRate
      });
      
      return processedSettings;
    }
    
    // New user - use global settings
    console.log("📭 No UserSettings document found, returning defaults");
    return {
      monthlyRate: globalRate,
      promoRates: [],
      referralBonus: 0,
      totalReferrals: 0,
      adminFeePercentage: 0,
      adminFeeFixed: 0,
      rateHistory: [],
      customSettings: {},
      isUsingGlobalRate: true,
      feeReason: null,
      lastReferralBonus: null
    };
  } catch (error) {
    console.error("❌ Error getting user settings:", error);
    // Return default values on error
    return {
      monthlyRate: 10,
      promoRates: [],
      referralBonus: 0,
      totalReferrals: 0,
      adminFeePercentage: 0,
      adminFeeFixed: 0,
      rateHistory: [],
      customSettings: {},
      isUsingGlobalRate: true,
      feeReason: null,
      lastReferralBonus: null
    };
  }
};

/**
 * ✅ FIXED: Update user-specific settings
 * This version doesn't merge with processed settings that include fallbacks
 */
export const updateUserSettings = async (userId, settings, adminId) => {
  try {
    console.log("🔄 Starting updateUserSettings for user:", userId);
    console.log("📝 Settings to update:", settings);
    
    const userSettingsRef = doc(firestore, "UserSettings", userId);
    
    // ✅ FIX: Get RAW document data, not processed settings
    const existingUserSettingsDoc = await getDoc(userSettingsRef);
    const existingRawSettings = existingUserSettingsDoc.exists() ? existingUserSettingsDoc.data() : {};
    
    console.log("📥 Existing raw UserSettings from DB:", existingRawSettings);
    
    // ✅ FIX: Only build rate history if rate is actually changing
    let rateHistory = existingRawSettings.rateHistory || [];
    
    if (settings.monthlyRate !== undefined) {
      const currentRateInDB = existingRawSettings.monthlyRate;
      if (currentRateInDB !== undefined && currentRateInDB !== settings.monthlyRate) {
        rateHistory = [...rateHistory, {
          previousRate: currentRateInDB,
          newRate: settings.monthlyRate,
          changedAt: new Date(),
          changedBy: adminId,
          reason: settings.rateChangeReason || "Admin adjustment"
        }];
        console.log("📈 Adding rate history entry:", rateHistory[rateHistory.length - 1]);
      }
    }
    
    // ✅ FIX: Build update data with only the fields being changed
    const updateData = {
      ...settings, // Only the fields being updated
      rateHistory,
      lastUpdated: serverTimestamp(),
      updatedBy: adminId
    };
    
    // Remove the reason fields as they're just for history
    delete updateData.rateChangeReason;
    delete updateData.feeReason;
    
    console.log("📤 Final update data being sent to Firestore:", updateData);
    
    // ✅ FIX: Use updateDoc for existing docs, setDoc for new ones
    if (existingUserSettingsDoc.exists()) {
      await updateDoc(userSettingsRef, updateData);
      console.log("✅ Updated existing UserSettings document");
    } else {
      await setDoc(userSettingsRef, updateData);
      console.log("✅ Created new UserSettings document");
    }
    
    // ✅ VERIFICATION: Read back the data to confirm it was saved
    console.log("⏳ Waiting 500ms before verification...");
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const verificationDoc = await getDoc(userSettingsRef);
    if (verificationDoc.exists()) {
      const verificationData = verificationDoc.data();
      console.log("🔍 Verification - Data after save:", verificationData);
      
      // Check if the values were actually saved
      let saveSuccessful = true;
      const issues = [];
      
      if (settings.monthlyRate !== undefined && verificationData.monthlyRate !== settings.monthlyRate) {
        console.error("❌ monthlyRate not saved correctly:", {
          expected: settings.monthlyRate,
          actual: verificationData.monthlyRate
        });
        issues.push(`monthlyRate: expected ${settings.monthlyRate}, got ${verificationData.monthlyRate}`);
        saveSuccessful = false;
      }
      
      if (settings.adminFeePercentage !== undefined && verificationData.adminFeePercentage !== settings.adminFeePercentage) {
        console.error("❌ adminFeePercentage not saved correctly:", {
          expected: settings.adminFeePercentage,
          actual: verificationData.adminFeePercentage
        });
        issues.push(`adminFeePercentage: expected ${settings.adminFeePercentage}, got ${verificationData.adminFeePercentage}`);
        saveSuccessful = false;
      }
      
      if (settings.adminFeeFixed !== undefined && verificationData.adminFeeFixed !== settings.adminFeeFixed) {
        console.error("❌ adminFeeFixed not saved correctly:", {
          expected: settings.adminFeeFixed,
          actual: verificationData.adminFeeFixed
        });
        issues.push(`adminFeeFixed: expected ${settings.adminFeeFixed}, got ${verificationData.adminFeeFixed}`);
        saveSuccessful = false;
      }
      
      if (!saveSuccessful) {
        return { 
          success: false, 
          error: `Data was not saved correctly: ${issues.join(', ')}`,
          verificationData
        };
      }
    } else {
      return {
        success: false,
        error: "Document not found after save - this indicates a serious database issue"
      };
    }
    
    console.log("✅ updateUserSettings completed successfully");
    return { success: true };
    
  } catch (error) {
    console.error("❌ Error in updateUserSettings:", error);
    console.error("Stack trace:", error.stack);
    return { success: false, error: error.message };
  }
};

/**
 * Get applicable rate for a specific time period considering promos
 */
export const getApplicableRate = (userSettings, startDate, endDate = new Date()) => {
  const baseRate = userSettings.monthlyRate;
  const promoRates = userSettings.promoRates || [];
  
  // Check if any promo period applies to this time range
  for (const promo of promoRates) {
    if (!promo.active) continue;
    
    const promoStart = promo.startDate.toDate ? promo.startDate.toDate() : new Date(promo.startDate);
    const promoEnd = promo.endDate.toDate ? promo.endDate.toDate() : new Date(promo.endDate);
    
    // Check if the period overlaps with promo period
    if (startDate < promoEnd && endDate > promoStart) {
      return {
        rate: promo.rate,
        isPromo: true,
        promoName: promo.name || "Special Rate",
        promoStart: promoStart,
        promoEnd: promoEnd,
        promoId: promo.id
      };
    }
  }
  
  // Apply referral bonus to base rate
  const referralBonus = userSettings.referralBonus || 0;
  const finalRate = baseRate + referralBonus;
  
  return {
    rate: finalRate,
    isPromo: false,
    hasReferralBonus: referralBonus > 0,
    referralBonus: referralBonus
  };
};

/**
 * Calculate daily compound rate from monthly percentage
 */
export const calculateDailyRate = (monthlyPercentage) => {
  return Math.pow(1 + monthlyPercentage / 100, 1 / 30) - 1;
};

/**
 * Enhanced current value calculation with user-specific rates, promos, and fees
 */
export const calculateCurrentValueEnhanced = async (principal, userId, startDate) => {
  const now = new Date();
  
  // Handle invalid startDate
  let start;
  try {
    if (!startDate) return principal;
    
    if (startDate.toDate && typeof startDate.toDate === 'function') {
      start = startDate.toDate();
    } else if (startDate instanceof Date) {
      start = startDate;
    } else {
      start = new Date(startDate);
    }
    
    if (isNaN(start.getTime())) return principal;
  } catch (error) {
    console.error('Error processing startDate:', error);
    return principal;
  }
  
  const msElapsed = now.getTime() - start.getTime();
  const daysElapsed = msElapsed / (1000 * 60 * 60 * 24);
  
  if (daysElapsed <= 0) return principal;
  
  // Get user settings
  const userSettings = await getUserSettings(userId);
  
  // Calculate growth with potentially different rates over time
  let currentValue = principal;
  let currentDate = new Date(start);
  
  // Split calculation into periods if there are rate changes or promos
  const monthsElapsed = daysElapsed / 30;
  
  for (let month = 0; month < Math.ceil(monthsElapsed); month++) {
    const periodStart = new Date(currentDate);
    const periodEnd = new Date(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days later
    
    if (periodEnd > now) {
      periodEnd.setTime(now.getTime());
    }
    
    const periodDays = (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24);
    
    if (periodDays <= 0) break;
    
    // Get applicable rate for this period
    const rateInfo = getApplicableRate(userSettings, periodStart, periodEnd);
    const dailyRate = calculateDailyRate(rateInfo.rate);
    
    // Apply growth for this period
    const periodGrowth = currentValue * (Math.pow(1 + dailyRate, periodDays) - 1);
    
    // Apply admin fees if applicable (monthly)
    if (month > 0 && periodDays >= 25) { // Only apply if close to full month
      const adminFeeFixed = userSettings.adminFeeFixed || 0;
      const adminFeePercentage = userSettings.adminFeePercentage || 0;
      
      const percentageFee = periodGrowth * (adminFeePercentage / 100);
      const totalFees = percentageFee + adminFeeFixed;
      
      currentValue = currentValue + Math.max(0, periodGrowth - totalFees);
    } else {
      currentValue = currentValue + periodGrowth;
    }
    
    currentDate = new Date(periodEnd);
  }
  
  return Math.max(principal, currentValue); // Never return less than principal
};

/**
 * Enhanced deposit stats calculation
 */
export const calculateDepositStatsEnhanced = async (deposit, userId = null) => {
  const userIdToUse = userId || deposit.userId;
  const currentValue = await calculateCurrentValueEnhanced(deposit.amount, userIdToUse, deposit.createdAt);
  const totalGain = currentValue - deposit.amount;
  const gainPercentage = (totalGain / deposit.amount) * 100;
  
  // Get user settings for additional info
  const userSettings = await getUserSettings(userIdToUse);
  const rateInfo = getApplicableRate(userSettings, deposit.createdAt.toDate ? deposit.createdAt.toDate() : new Date(deposit.createdAt));
  
  return {
    currentValue,
    totalGain,
    gainPercentage,
    dailyGain: totalGain / Math.max(1, getDaysElapsed(deposit.createdAt)),
    appliedRate: rateInfo.rate,
    isPromoRate: rateInfo.isPromo,
    hasReferralBonus: rateInfo.hasReferralBonus,
    userSettings,
    rateInfo
  };
};

/**
 * Create or update promo rate for user
 */
export const addPromoRate = async (userId, promoData, adminId) => {
  try {
    const userSettings = await getUserSettings(userId);
    const promoRates = userSettings.promoRates || [];
    
    const newPromo = {
      id: Date.now().toString(),
      name: promoData.name,
      rate: parseFloat(promoData.rate),
      startDate: new Date(promoData.startDate),
      endDate: new Date(promoData.endDate),
      createdAt: serverTimestamp(),
      createdBy: adminId,
      active: true
    };
    
    promoRates.push(newPromo);
    
    await updateUserSettings(userId, { promoRates }, adminId);
    return { success: true, promoId: newPromo.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Add referral bonus to user
 */
export const addReferralBonus = async (userId, bonusPercentage, adminId, reason = "") => {
  try {
    const userSettings = await getUserSettings(userId);
    const currentBonus = userSettings.referralBonus || 0;
    const totalReferrals = (userSettings.totalReferrals || 0) + 1;
    
    // ✅ FIX: Update both UserSettings AND the user's Investors document
    const newSettings = {
      referralBonus: currentBonus + parseFloat(bonusPercentage),
      totalReferrals: totalReferrals,
      lastReferralBonus: {
        amount: parseFloat(bonusPercentage),
        addedAt: new Date(),
        addedBy: adminId,
        reason: reason
      }
    };
    
    // Update UserSettings
    await updateUserSettings(userId, newSettings, adminId);
    
    // ✅ ALSO update the referralStats in the Investors collection
    const userRef = doc(db, "Investors", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const currentReferralStats = userDoc.data().referralStats || {
        totalReferrals: 0,
        successfulReferrals: 0,
        totalBonusEarned: 0
      };
      
      // Calculate how many referrals this bonus represents
      // Each referral = 0.5% bonus
      const referralsProcessed = Math.round(parseFloat(bonusPercentage) / 0.5);
      
      await updateDoc(userRef, {
        "referralStats.totalReferrals": currentReferralStats.totalReferrals + referralsProcessed,
        "referralStats.successfulReferrals": currentReferralStats.successfulReferrals + referralsProcessed,
        "referralStats.totalBonusEarned": (currentReferralStats.totalBonusEarned || 0) + parseFloat(bonusPercentage),
        "referralStats.lastUpdated": new Date()
      });
      
      console.log(`✅ Updated referralStats for user ${userId}: +${referralsProcessed} referrals`);
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error adding referral bonus:", error);
    return { success: false, error: error.message };
  }
};
/**
 * Set admin fees for user
 */
export const setAdminFees = async (userId, feeData, adminId) => {
  try {
    const newSettings = {
      adminFeePercentage: parseFloat(feeData.percentage || 0),
      adminFeeFixed: parseFloat(feeData.fixed || 0),
      feeReason: feeData.reason || "",
      feeSetAt: serverTimestamp()
    };
    
    await updateUserSettings(userId, newSettings, adminId);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get days elapsed since deposit creation
 */
export const getDaysElapsed = (startDate) => {
  const now = new Date();
  
  if (!startDate) return 0;
  
  let start;
  try {
    if (startDate.toDate && typeof startDate.toDate === 'function') {
      start = startDate.toDate();
    } else if (startDate instanceof Date) {
      start = startDate;
    } else {
      start = new Date(startDate);
    }
    
    if (isNaN(start.getTime())) return 0;
  } catch (error) {
    console.error('Error processing startDate in getDaysElapsed:', error);
    return 0;
  }
  
  return Math.max(0, Math.floor((now - start) / (1000 * 60 * 60 * 24)));
};

/**
 * Calculate enhanced portfolio totals with user-specific rates
 */
export const calculatePortfolioTotalsEnhanced = async (deposits, userId) => {
  let totalInvested = 0;
  let totalCurrentValue = 0;
  let confirmedDepositsCount = 0;
  let totalFeesApplied = 0;

  const depositPromises = deposits
    .filter(deposit => deposit.status === 'confirmed')
    .map(async (deposit) => {
      totalInvested += deposit.amount;
      const stats = await calculateDepositStatsEnhanced(deposit, userId);
      totalCurrentValue += stats.currentValue;
      confirmedDepositsCount++;
      return stats;
    });

  await Promise.all(depositPromises);

  const totalGain = totalCurrentValue - totalInvested;
  const totalGainPercentage = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;

  // Get user settings for display
  const userSettings = await getUserSettings(userId);

  return {
    totalInvested,
    totalCurrentValue,
    totalGain,
    totalGainPercentage,
    confirmedDepositsCount,
    totalFeesApplied,
    userSettings,
    averageDailyGain: totalGain / Math.max(1, getAverageInvestmentDays(deposits))
  };
};

/**
 * Enhanced withdrawal eligibility with user-specific rates
 */
export const calculateWithdrawalEligibilityEnhanced = async (deposits, userId) => {
  const now = new Date();
  const oneMonthMs = 30 * 24 * 60 * 60 * 1000;
  const sixMonthsMs = 180 * 24 * 60 * 60 * 1000;
  
  let totalCapital = 0;
  let totalCurrentValue = 0;
  let availableProfit = 0;
  let availableCapital = 0;
  let earliestWithdrawalDate = null;
  let fullWithdrawalDate = null;
  let eligibleDeposits = [];
  let lockedDeposits = [];

  const confirmedDeposits = deposits.filter(d => d.status === 'confirmed' && d.createdAt);

  const depositPromises = confirmedDeposits.map(async (deposit) => {
    const depositDate = deposit.createdAt.toDate ? deposit.createdAt.toDate() : new Date(deposit.createdAt);
    const daysSinceDeposit = (now - depositDate) / (1000 * 60 * 60 * 24);
    
    // Calculate current value with user-specific rates
    const currentValue = await calculateCurrentValueEnhanced(deposit.amount, userId, deposit.createdAt);
    const profit = currentValue - deposit.amount;
    
    totalCapital += deposit.amount;
    totalCurrentValue += currentValue;

    // Check eligibility
    const hasMinimumTime = daysSinceDeposit >= 30;
    const canWithdrawCapital = daysSinceDeposit >= 180;
    
    if (hasMinimumTime) {
      availableProfit += profit;
      
      if (canWithdrawCapital) {
        availableCapital += deposit.amount;
        eligibleDeposits.push({
          ...deposit,
          daysSinceDeposit: Math.floor(daysSinceDeposit),
          currentValue,
          profit,
          canWithdrawCapital: true,
          canWithdrawProfit: true
        });
      } else {
        eligibleDeposits.push({
          ...deposit,
          daysSinceDeposit: Math.floor(daysSinceDeposit),
          currentValue,
          profit,
          canWithdrawCapital: false,
          canWithdrawProfit: true,
          daysUntilCapitalAccess: 180 - Math.floor(daysSinceDeposit)
        });
      }
    } else {
      lockedDeposits.push({
        ...deposit,
        daysSinceDeposit: Math.floor(daysSinceDeposit),
        currentValue,
        profit,
        canWithdrawCapital: false,
        canWithdrawProfit: false,
        daysUntilProfitAccess: 30 - Math.floor(daysSinceDeposit),
        daysUntilCapitalAccess: 180 - Math.floor(daysSinceDeposit)
      });
    }

    // Track dates
    const withdrawalDate = new Date(depositDate.getTime() + oneMonthMs);
    const capitalDate = new Date(depositDate.getTime() + sixMonthsMs);
    
    if (!earliestWithdrawalDate || withdrawalDate < earliestWithdrawalDate) {
      earliestWithdrawalDate = withdrawalDate;
    }
    if (!fullWithdrawalDate || capitalDate > fullWithdrawalDate) {
      fullWithdrawalDate = capitalDate;
    }
  });

  await Promise.all(depositPromises);

  const totalProfit = totalCurrentValue - totalCapital;
  const totalAvailableForWithdrawal = availableProfit + availableCapital;
  
  const canMakeAnyWithdrawal = eligibleDeposits.length > 0;
  const hasLockedFunds = lockedDeposits.length > 0;
  const canWithdrawAllCapital = availableCapital === totalCapital;

  return {
    totalCapital,
    totalCurrentValue,
    totalProfit,
    availableProfit,
    availableCapital,
    totalAvailableForWithdrawal,
    canMakeAnyWithdrawal,
    canWithdrawAllCapital,
    hasLockedFunds,
    eligibleDeposits,
    lockedDeposits,
    earliestWithdrawalDate,
    fullWithdrawalDate,
    eligibleDepositsCount: eligibleDeposits.length,
    lockedDepositsCount: lockedDeposits.length,
    daysUntilFullAccess: fullWithdrawalDate ? Math.max(0, Math.ceil((fullWithdrawalDate - now) / (1000 * 60 * 60 * 24))) : 0
  };
};

/**
 * Validate withdrawal request against rules
 */
export const validateWithdrawalRequest = (requestedAmount, requestType, eligibility) => {
  const errors = [];
  
  if (!eligibility.canMakeAnyWithdrawal) {
    errors.push("No deposits are eligible for withdrawal yet. Minimum investment period is 30 days.");
    return { isValid: false, errors };
  }
  
  if (requestedAmount <= 0) {
    errors.push("Withdrawal amount must be greater than 0.");
  }
  
  if (requestedAmount > eligibility.totalAvailableForWithdrawal) {
    errors.push(`Maximum available for withdrawal is ${eligibility.totalAvailableForWithdrawal.toFixed(2)}.`);
  }
  
  if (requestType === "profit_only") {
    if (requestedAmount > eligibility.availableProfit) {
      errors.push(`Maximum profit available for withdrawal is ${eligibility.availableProfit.toFixed(2)}.`);
    }
  }
  
  if (requestType === "capital") {
    if (requestedAmount > eligibility.availableCapital) {
      errors.push(`Maximum capital available for withdrawal is ${eligibility.availableCapital.toFixed(2)}.`);
    }
    if (!eligibility.canWithdrawAllCapital && eligibility.hasLockedFunds) {
      errors.push("Some deposits have not reached the 6-month capital withdrawal period.");
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get average investment days across all deposits
 */
const getAverageInvestmentDays = (deposits) => {
  const confirmedDeposits = deposits.filter(d => d.status === 'confirmed');
  if (confirmedDeposits.length === 0) return 1;
  
  const totalDays = confirmedDeposits.reduce((sum, deposit) => {
    return sum + getDaysElapsed(deposit.createdAt);
  }, 0);
  
  return totalDays / confirmedDeposits.length;
};

// =============================================================================
// BACKWARD COMPATIBILITY FUNCTIONS
// =============================================================================

/**
 * Get or set monthly growth rate from admin settings
 */
export const getMonthlyGrowthRate = async () => {
  try {
    const settingsDoc = await getDoc(doc(firestore, "Settings", "general"));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      return data.monthlyGrowthRate || 10; // Default 10%
    }
    return 10;
  } catch (error) {
    console.error("Error fetching growth rate:", error);
    return 10;
  }
};

/**
 * Admin function to update monthly growth rate
 */
export const updateMonthlyGrowthRate = async (newRate, adminId) => {
  try {
    const settingsRef = doc(firestore, "Settings", "general");
    await updateDoc(settingsRef, {
      monthlyGrowthRate: parseFloat(newRate),
      lastUpdated: serverTimestamp(),
      updatedBy: adminId
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating growth rate:", error);
    return { success: false, error: error.message };
  }
};

// Legacy function for backward compatibility
export const calculateCurrentValue = (principal, monthlyRate, startDate) => {
  const now = new Date();
  
  let start;
  try {
    if (!startDate) return principal;
    
    if (startDate.toDate && typeof startDate.toDate === 'function') {
      start = startDate.toDate();
    } else if (startDate instanceof Date) {
      start = startDate;
    } else {
      start = new Date(startDate);
    }
    
    if (isNaN(start.getTime())) return principal;
  } catch (error) {
    console.error('Error processing startDate:', error);
    return principal;
  }
  
  const msElapsed = now.getTime() - start.getTime();
  const daysElapsed = msElapsed / (1000 * 60 * 60 * 24);
  
  if (daysElapsed <= 0) return principal;
  
  const dailyRate = calculateDailyRate(monthlyRate);
  return principal * Math.pow(1 + dailyRate, daysElapsed);
};

export const calculateDepositStats = (deposit, monthlyRate) => {
  const currentValue = calculateCurrentValue(deposit.amount, monthlyRate, deposit.createdAt);
  const totalGain = currentValue - deposit.amount;
  const gainPercentage = (totalGain / deposit.amount) * 100;
  
  return {
    currentValue,
    totalGain,
    gainPercentage,
    dailyGain: totalGain / Math.max(1, getDaysElapsed(deposit.createdAt))
  };
};

/**
 * Real-time listener for user deposits
 */
export const subscribeToUserDeposits = (userId, callback) => {
  const depositsCollection = collection(firestore, "Deposits");
  const q = query(depositsCollection, where("userId", "==", userId));
  
  return onSnapshot(q, (snapshot) => {
    const deposits = [];
    snapshot.forEach((doc) => {
      deposits.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by creation date (newest first)
    deposits.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    
    callback(deposits);
  });
};

/**
 * Calculate portfolio totals for a user (legacy)
 */
export const calculatePortfolioTotals = (deposits, monthlyRate) => {
  let totalInvested = 0;
  let totalCurrentValue = 0;
  let confirmedDepositsCount = 0;

  deposits.forEach(deposit => {
    if (deposit.status === 'confirmed') {
      totalInvested += deposit.amount;
      const stats = calculateDepositStats(deposit, monthlyRate);
      totalCurrentValue += stats.currentValue;
      confirmedDepositsCount++;
    }
  });

  const totalGain = totalCurrentValue - totalInvested;
  const totalGainPercentage = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;

  return {
    totalInvested,
    totalCurrentValue,
    totalGain,
    totalGainPercentage,
    confirmedDepositsCount,
    averageDailyGain: totalGain / Math.max(1, getAverageInvestmentDays(deposits))
  };
};

/**
 * Calculate withdrawal eligibility (legacy)
 */
export const calculateWithdrawalEligibility = (deposits, monthlyRate) => {
  const now = new Date();
  const oneMonthMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  const sixMonthsMs = 180 * 24 * 60 * 60 * 1000; // 180 days in milliseconds
  
  let totalCapital = 0;
  let totalCurrentValue = 0;
  let availableProfit = 0;
  let availableCapital = 0;
  let earliestWithdrawalDate = null;
  let fullWithdrawalDate = null;
  let eligibleDeposits = [];
  let lockedDeposits = [];

  const confirmedDeposits = deposits.filter(d => d.status === 'confirmed' && d.createdAt);

  confirmedDeposits.forEach(deposit => {
    const depositDate = deposit.createdAt.toDate ? deposit.createdAt.toDate() : new Date(deposit.createdAt);
    const daysSinceDeposit = (now - depositDate) / (1000 * 60 * 60 * 24);
    
    // Calculate current value and profit for this deposit
    const currentValue = calculateCurrentValue(deposit.amount, monthlyRate, deposit.createdAt);
    const profit = currentValue - deposit.amount;
    
    totalCapital += deposit.amount;
    totalCurrentValue += currentValue;

    // Check eligibility for this deposit
    const hasMinimumTime = daysSinceDeposit >= 30; // 1 month minimum
    const canWithdrawCapital = daysSinceDeposit >= 180; // 6 months for capital
    
    if (hasMinimumTime) {
      // Can withdraw profit from this deposit
      availableProfit += profit;
      
      if (canWithdrawCapital) {
        // Can also withdraw capital from this deposit
        availableCapital += deposit.amount;
        eligibleDeposits.push({
          ...deposit,
          daysSinceDeposit: Math.floor(daysSinceDeposit),
          currentValue,
          profit,
          canWithdrawCapital: true,
          canWithdrawProfit: true
        });
      } else {
        // Can only withdraw profit
        eligibleDeposits.push({
          ...deposit,
          daysSinceDeposit: Math.floor(daysSinceDeposit),
          currentValue,
          profit,
          canWithdrawCapital: false,
          canWithdrawProfit: true,
          daysUntilCapitalAccess: 180 - Math.floor(daysSinceDeposit)
        });
      }
    } else {
      // Deposit is locked (less than 1 month)
      lockedDeposits.push({
        ...deposit,
        daysSinceDeposit: Math.floor(daysSinceDeposit),
        currentValue,
        profit,
        canWithdrawCapital: false,
        canWithdrawProfit: false,
        daysUntilProfitAccess: 30 - Math.floor(daysSinceDeposit),
        daysUntilCapitalAccess: 180 - Math.floor(daysSinceDeposit)
      });
    }

    // Track earliest dates
    const withdrawalDate = new Date(depositDate.getTime() + oneMonthMs);
    const capitalDate = new Date(depositDate.getTime() + sixMonthsMs);
    
    if (!earliestWithdrawalDate || withdrawalDate < earliestWithdrawalDate) {
      earliestWithdrawalDate = withdrawalDate;
    }
    if (!fullWithdrawalDate || capitalDate > fullWithdrawalDate) {
      fullWithdrawalDate = capitalDate;
    }
  });

  const totalProfit = totalCurrentValue - totalCapital;
  const totalAvailableForWithdrawal = availableProfit + availableCapital;
  
  // Overall eligibility
  const canMakeAnyWithdrawal = eligibleDeposits.length > 0;
  const hasLockedFunds = lockedDeposits.length > 0;
  const canWithdrawAllCapital = availableCapital === totalCapital;

  return {
    // Totals
    totalCapital,
    totalCurrentValue,
    totalProfit,
    
    // Available amounts
    availableProfit,
    availableCapital,
    totalAvailableForWithdrawal,
    
    // Eligibility status
    canMakeAnyWithdrawal,
    canWithdrawAllCapital,
    hasLockedFunds,
    
    // Deposit breakdowns
    eligibleDeposits,
    lockedDeposits,
    
    // Important dates
    earliestWithdrawalDate,
    fullWithdrawalDate,
    
    // Summary stats
    eligibleDepositsCount: eligibleDeposits.length,
    lockedDepositsCount: lockedDeposits.length,
    
    // Time until full access
    daysUntilFullAccess: fullWithdrawalDate ? Math.max(0, Math.ceil((fullWithdrawalDate - now) / (1000 * 60 * 60 * 24))) : 0
  };
};

/**
 * Admin utility to create a new deposit
 */
export const createDepositForUser = async (userId, depositData, adminId) => {
  try {
    const depositsCollection = collection(firestore, "Deposits");
    
    const newDeposit = {
      userId,
      amount: parseFloat(depositData.amount),
      currency: depositData.currency || "₦",
      status: "confirmed", // Admin creates confirmed deposits
      createdAt: serverTimestamp(),
      confirmedAt: serverTimestamp(),
      confirmedBy: adminId,
      description: depositData.description || "",
      paymentMethod: depositData.paymentMethod || "",
      transactionId: depositData.transactionId || "",
      metadata: {
        createdByAdmin: true,
        adminNotes: depositData.adminNotes || ""
      }
    };

    const docRef = await addDoc(depositsCollection, newDeposit);
    return { success: true, depositId: docRef.id };
  } catch (error) {
    console.error("Error creating deposit:", error);
    return { success: false, error: error.message };
  }
};